import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  NgxsOnInit,
  State,
  StateContext,
} from '@ngxs/store';
import { ColumnState } from 'ag-grid-community';
import { SetColumnSetting, SetPageSizeSetting } from './columns.action';

export interface AgGridSettings {
  columnStates?: ColumnState[];
  pageSize?: number;
}

interface AgGridColumnSettingsModel {
  version: number;
  settings: Record<string, Record<string, AgGridSettings>>;
}

const CURRENT_VERSION = 2;

const defaults: AgGridColumnSettingsModel = {
  version: CURRENT_VERSION,
  settings: {},
};

@State<AgGridColumnSettingsModel>({
  name: 'AgGridColumnSettings',
  defaults,
})
@Injectable()
export class AgGridColumnSettingsState implements NgxsOnInit {
  ngxsOnInit(ctx: StateContext<AgGridColumnSettingsModel>): void {
    const state = ctx.getState();
    if ((state.version ?? 0) >= CURRENT_VERSION) return;

    const migratedSettings: Record<string, Record<string, AgGridSettings>> = {};
    for (const userId of Object.keys(state.settings ?? {})) {
      migratedSettings[userId] = {};
      for (const gridId of Object.keys(state.settings[userId])) {
        const value = state.settings[userId][gridId];
        migratedSettings[userId][gridId] = Array.isArray(value)
          ? { columnStates: value }
          : value;
      }
    }
    ctx.setState({ version: CURRENT_VERSION, settings: migratedSettings });
  }

  static columnStates({
    userId,
    gridId,
  }: {
    userId: string;
    gridId: string;
  }): (state: AgGridColumnSettingsModel) => ColumnState[] | undefined {
    return createSelector(
      [AgGridColumnSettingsState],
      (state: AgGridColumnSettingsModel) => {
        return state.settings[userId]?.[gridId]?.columnStates;
      },
    );
  }

  static pageSize({
    userId,
    gridId,
  }: {
    userId: string;
    gridId: string;
  }): (state: AgGridColumnSettingsModel) => number | undefined {
    return createSelector(
      [AgGridColumnSettingsState],
      (state: AgGridColumnSettingsModel) => {
        return state.settings[userId]?.[gridId]?.pageSize;
      },
    );
  }

  @Action(SetColumnSetting)
  setColumnSetting(
    ctx: StateContext<AgGridColumnSettingsModel>,
    { payload }: SetColumnSetting,
  ): void {
    const existing =
      ctx.getState().settings[payload.userId]?.[payload.gridId] ?? {};
    ctx.patchState({
      settings: {
        ...ctx.getState().settings,
        [payload.userId]: {
          ...ctx.getState().settings[payload.userId],
          [payload.gridId]: { ...existing, columnStates: payload.columnStates },
        },
      },
    });
  }

  @Action(SetPageSizeSetting)
  setPageSizeSetting(
    ctx: StateContext<AgGridColumnSettingsModel>,
    { payload }: SetPageSizeSetting,
  ): void {
    const existing =
      ctx.getState().settings[payload.userId]?.[payload.gridId] ?? {};
    ctx.patchState({
      settings: {
        ...ctx.getState().settings,
        [payload.userId]: {
          ...ctx.getState().settings[payload.userId],
          [payload.gridId]: { ...existing, pageSize: payload.pageSize },
        },
      },
    });
  }
}
